import React, { useState, useEffect } from 'react';
import { getUserTeamNamingDefaults, updateUserTeamNamingDefaults, getTeamNaming, updateTeamNaming } from '../../DatabaseAPI';

function TeamEditModal({ isOpen, onClose, onSave, team }) {
    const [teamName, setTeamName] = useState('');
    const [knownAs, setKnownAs] = useState('');
    const [hideKnownAs, setHideKnownAs] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(true);
    const [canEditTeamName, setCanEditTeamName] = useState(false);

    // Load team data and user defaults when modal opens
    useEffect(() => {
        if (isOpen && team) {
            loadTeamData();
        }
    }, [isOpen, team]);

    const loadTeamData = async () => {
        setLoading(true);
        setError('');
        
        try {
            // Load current team naming data
            const teamNamingData = await getTeamNaming(team.id);
            
            // Load user defaults
            const userDefaults = await getUserTeamNamingDefaults();
            
            // Set team's current values from the nested 'team' object
            const teamData = teamNamingData.team || teamNamingData; // Handle both response formats
            setTeamName(teamData.name || '');
            setKnownAs(teamData.known_as || '');
            setHideKnownAs(teamData.hide_known_as || false);
            setCanEditTeamName(teamData.can_edit_name || false);
            
            console.log('TeamEditModal: Loaded team naming data:', teamNamingData);
            console.log('TeamEditModal: Can edit team name:', teamNamingData.can_edit_name);
        } catch (error) {
            console.error('TeamEditModal: Error loading team data:', error);
            setError('Failed to load team data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Update user defaults when fields change
    const updateUserDefaults = async (newTeamName, newKnownAs, newHideKnownAs) => {
        try {
            await updateUserTeamNamingDefaults({
                team_name: newTeamName,
                known_as: newKnownAs,
                hide_known_as: newHideKnownAs
            });
        } catch (error) {
            console.error('TeamEditModal: Error updating user defaults:', error);
            // Don't show error to user for this - it's background functionality
        }
    };

    const handleTeamNameChange = (newTeamName) => {
        setTeamName(newTeamName);
        updateUserDefaults(newTeamName, knownAs, hideKnownAs);
    };

    const handleKnownAsChange = (newKnownAs) => {
        setKnownAs(newKnownAs);
        updateUserDefaults(teamName, newKnownAs, hideKnownAs);
    };

    const handleHideKnownAsChange = (newHide) => {
        setHideKnownAs(newHide);
        updateUserDefaults(teamName, knownAs, newHide);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError('');

        if (!canEditTeamName) {
            setError('Team names cannot be edited at this time. The league is no longer in PRE_GAMES (EQUIP) phase.');
            setIsSubmitting(false);
            return;
        }

        if (!teamName.trim()) {
            setError('Team name is required');
            setIsSubmitting(false);
            return;
        }

        if (!knownAs.trim()) {
            setError('Profile Name is required');
            setIsSubmitting(false);
            return;
        }

        try {
            const teamData = {
                name: teamName.trim(),
                known_as: knownAs.trim(),
                hide_known_as: hideKnownAs
            };

            console.log('TeamEditModal: Submitting team data:', teamData);

            const response = await updateTeamNaming(team.id, teamData);
            
            console.log('TeamEditModal: Team naming updated successfully:', response);

            // Call onSave callback with updated team data
            if (onSave) {
                onSave({
                    ...team,
                    name: teamData.name,
                    known_as: teamData.known_as,
                    hide_known_as: teamData.hide_known_as,
                    display_name: response.team.display_name  // CRITICAL FIX: Use nested field from API response
                });
            }

            onClose();
        } catch (error) {
            console.error('TeamEditModal: Error updating team naming:', error);
            setError(error.message || 'Failed to update team name. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getDisplayName = () => {
        if (knownAs && !hideKnownAs) {
            return `${teamName} (${knownAs})`;
        }
        return teamName;
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 10000,
            padding: '20px'
        }}>
            <div style={{
                backgroundColor: '#1a202c',
                borderRadius: '12px',
                padding: '40px',
                width: '100%',
                maxWidth: '600px',
                color: 'white',
                position: 'relative',
                border: '2px solid #4a5568',
                maxHeight: '90vh',
                overflowY: 'auto'
            }}>
                {/* Close Button */}
                <button
                    onClick={onClose}
                    disabled={isSubmitting}
                    style={{
                        position: 'absolute',
                        top: '16px',
                        right: '16px',
                        background: 'none',
                        border: 'none',
                        color: '#9CA3AF',
                        fontSize: '24px',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        padding: '4px',
                        borderRadius: '4px',
                        lineHeight: 1
                    }}
                >
                    ×
                </button>

                {/* Header */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '30px'
                }}>
                    <h1 style={{
                        fontSize: '28px',
                        fontWeight: 'bold',
                        color: '#48bb78',
                        margin: '0 0 8px 0'
                    }}>
                        EDIT TEAM NAME
                    </h1>
                    <p style={{
                        fontSize: '16px',
                        color: '#a0aec0',
                        margin: '0'
                    }}>
                        Update your team's name and visibility
                    </p>
                </div>

                {loading ? (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        color: '#a0aec0'
                    }}>
                        <div style={{ fontSize: '18px', marginBottom: '10px' }}>⏳</div>
                        Loading team data...
                    </div>
                ) : !canEditTeamName ? (
                    <div style={{
                        backgroundColor: '#e53e3e',
                        color: 'white',
                        padding: '20px',
                        borderRadius: '8px',
                        marginBottom: '30px',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '20px', marginBottom: '10px' }}>🔒</div>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', marginBottom: '8px' }}>
                            Team Names Locked
                        </div>
                        <div style={{ fontSize: '14px' }}>
                            Team names cannot be edited once the league moves beyond PRE_GAMES (EQUIP) phase.
                            Your user defaults will still be updated for future teams.
                        </div>
                    </div>
                ) : null}

                <form onSubmit={handleSubmit}>
                    {/* Error Message */}
                    {error && (
                        <div style={{
                            backgroundColor: '#e53e3e',
                            color: 'white',
                            padding: '12px',
                            borderRadius: '6px',
                            marginBottom: '20px',
                            textAlign: 'center',
                            fontSize: '14px'
                        }}>
                            {error}
                        </div>
                    )}

                    {/* Preview */}
                    <div style={{
                        backgroundColor: '#2d3748',
                        padding: '16px',
                        borderRadius: '8px',
                        marginBottom: '30px',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '14px', color: '#a0aec0', marginBottom: '8px' }}>
                            Preview:
                        </div>
                        <div style={{ 
                            fontSize: '20px', 
                            fontWeight: 'bold', 
                            color: '#48bb78'
                        }}>
                            {getDisplayName()}
                        </div>
                    </div>

                    {/* Dual Field Interface */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '20px',
                        marginBottom: '30px'
                    }}>
                        {/* Left Side: Team Name */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: '#f7fafc',
                                marginBottom: '8px',
                                textAlign: 'center'
                            }}>
                                Team Name
                            </label>
                            
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <input
                                    type="text"
                                    value={teamName}
                                    onChange={(e) => handleTeamNameChange(e.target.value)}
                                    disabled={!canEditTeamName || isSubmitting}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        fontSize: '16px',
                                        border: `2px solid ${!canEditTeamName ? '#4a5568' : '#4a5568'}`,
                                        borderRadius: '8px',
                                        backgroundColor: !canEditTeamName ? '#2d3748' : '#4a5568',
                                        color: !canEditTeamName ? '#a0aec0' : '#f7fafc',
                                        textAlign: 'center'
                                    }}
                                    placeholder="Enter team name"
                                />
                            </div>
                        </div>

                        {/* Right Side: Known As */}
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: '#f7fafc',
                                marginBottom: '8px',
                                textAlign: 'center'
                            }}>
                                Profile Name
                            </label>
                            
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <input
                                    type="text"
                                    value={knownAs}
                                    onChange={(e) => handleKnownAsChange(e.target.value)}
                                    disabled={isSubmitting}
                                    style={{
                                        width: '100%',
                                        padding: '12px',
                                        fontSize: '16px',
                                        border: '2px solid #4a5568',
                                        borderRadius: '8px',
                                        backgroundColor: '#4a5568',
                                        color: '#f7fafc',
                                        textAlign: 'center'
                                    }}
                                    placeholder="Enter profile name"
                                />
                                
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <input
                                        type="checkbox"
                                        id="hideKnownAs"
                                        checked={hideKnownAs}
                                        onChange={(e) => handleHideKnownAsChange(e.target.checked)}
                                        disabled={isSubmitting}
                                        style={{
                                            transform: 'scale(1.2)'
                                        }}
                                    />
                                    <label
                                        htmlFor="hideKnownAs"
                                        style={{
                                            fontSize: '14px',
                                            color: '#a0aec0',
                                            cursor: isSubmitting ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        Hide profile name
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '16px'
                    }}>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isSubmitting}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: '#4a5568',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                opacity: isSubmitting ? 0.6 : 1
                            }}
                        >
                            Cancel
                        </button>

                        <button
                            type="submit"
                            disabled={isSubmitting || !teamName.trim() || !knownAs.trim() || !canEditTeamName}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: (!teamName.trim() || !knownAs.trim() || isSubmitting || !canEditTeamName) ? '#4a5568' : '#48bb78',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: (!teamName.trim() || !knownAs.trim() || isSubmitting || !canEditTeamName) ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: 'bold',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                opacity: (!teamName.trim() || !knownAs.trim() || isSubmitting || !canEditTeamName) ? 0.6 : 1
                            }}
                        >
                            {isSubmitting ? '⏳ Updating...' : canEditTeamName ? '✅ Save Changes' : '🔒 Locked'}
                        </button>
                    </div>

                    {/* Help Text */}
                    <div style={{
                        marginTop: '20px',
                        padding: '12px',
                        backgroundColor: '#2d3748',
                        borderRadius: '6px',
                        fontSize: '12px',
                        color: '#a0aec0',
                        textAlign: 'center'
                    }}>
                        {canEditTeamName ? (
                            <>Your preferences will be saved for future teams. Team names can only be edited during PRE_GAMES (EQUIP) phase.</>
                        ) : (
                            <>Your preferences are being saved for future teams, but this team's name is locked.</>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}

export default TeamEditModal;